import { runInAction } from "mobx";

import {
  DEFAULT_SKILL_DESCRIPTION,
  DEFAULT_SKILL_NAME,
  FRONTMATTER_KEY_DESCRIPTION,
  FRONTMATTER_KEY_DISABLE_MODEL_INVOCATION,
  FRONTMATTER_KEY_NAME,
  SkillCollectionFileName,
} from "../constants";
import { PROVIDER_CONFIGS, type SkillMeta } from "../provider-config";
import type { SkillCollectionPreset } from "../skill-presets";
import type { SkillCollectionEditorStore } from "../store/skill-collection-editor-store";

import { splitSkillMarkdown, parseSkillFrontmatterEntries } from "./skill-frontmatter";

const parseSkillMeta = (content: string): SkillMeta => {
  const { frontmatter } = splitSkillMarkdown(content);
  const entries = parseSkillFrontmatterEntries(frontmatter);

  const get = (key: string) => entries.find(e => e.key === key)?.value;

  return {
    name: get(FRONTMATTER_KEY_NAME) ?? DEFAULT_SKILL_NAME,
    description: get(FRONTMATTER_KEY_DESCRIPTION) ?? DEFAULT_SKILL_DESCRIPTION,
    disableModelInvocation: get(FRONTMATTER_KEY_DISABLE_MODEL_INVOCATION) === "true",
  };
};

const getProviderDef = (preset: SkillCollectionPreset) => PROVIDER_CONFIGS[preset];

const extractAgnosticSkillFolderNames = (store: SkillCollectionEditorStore): string[] => {
  const names = new Set<string>();

  store.filePaths.forEach(path => {
    const segments = path.split("/").filter(Boolean);
    if (segments.length < 2) {
      return;
    }

    if (segments.at(-1)?.toUpperCase() !== SkillCollectionFileName.SkillMd.toUpperCase()) {
      return;
    }

    names.add(segments[0]);
  });

  return Array.from(names);
};

const extractSkillFolderNames = (
  store: SkillCollectionEditorStore,
  preset: SkillCollectionPreset,
): string[] => {
  const def = getProviderDef(preset);
  const prefix = def.skillRoot ? `${def.skillRoot}/` : "";
  const infraSet = new Set(def.infrastructurePaths);

  const names = new Set<string>();
  store.filePaths.forEach(p => {
    if (prefix && !p.startsWith(prefix)) {
      return;
    }
    const relative = prefix ? p.slice(prefix.length) : p;

    const topSegment = relative.split("/")[0];
    if (!topSegment || !relative.includes("/")) {
      return;
    }

    if (topSegment === def.configFile) {
      return;
    }

    if (infraSet.has(topSegment) || infraSet.has(`${topSegment}/`)) {
      return;
    }
    if (!prefix && infraSet.has(relative)) {
      return;
    }

    names.add(topSegment);
  });

  return Array.from(names);
};

const migrateConfigFile = (
  store: SkillCollectionEditorStore,
  oldDef: (typeof PROVIDER_CONFIGS)[SkillCollectionPreset] | null,
  newDef: (typeof PROVIDER_CONFIGS)[SkillCollectionPreset] | null,
): void => {
  const oldConfig = oldDef?.configFile;
  const newConfig = newDef?.configFile;

  if (!newDef) {
    if (oldConfig && store.filePaths.has(oldConfig)) {
      store.deleteNode(oldConfig, "file");
    }
    return;
  }

  if (!newConfig) {
    return;
  }

  if (!oldConfig || !store.filePaths.has(oldConfig)) {
    const defaultConfigFile = newDef.presetFiles.find(file => file.path === newDef.configFile);
    if (defaultConfigFile && !store.filePaths.has(newDef.configFile)) {
      store.createFile(newDef.configFile, { initialContent: defaultConfigFile.content });
    }
    return;
  }

  if (oldConfig === newConfig) {
    return;
  }

  store.renameNode(oldConfig, "file", newConfig);
};

const deleteInfrastructure = (
  store: SkillCollectionEditorStore,
  infrastructurePaths: string[],
): void => {
  for (const infraPath of infrastructurePaths) {
    if (infraPath.endsWith("/")) {
      const folderPath = infraPath.slice(0, -1);
      store.deleteNode(folderPath, "folder");
    } else {
      if (store.filePaths.has(infraPath)) {
        store.deleteNode(infraPath, "file");
      }
    }
  }
};

const convertSkillInternals = (
  store: SkillCollectionEditorStore,
  oldDef: (typeof PROVIDER_CONFIGS)[SkillCollectionPreset] | null,
  opts: {
    newDef: (typeof PROVIDER_CONFIGS)[SkillCollectionPreset] | null;
    skillNames: string[];
    newPrefix: string;
  },
): void => {
  const { newDef, skillNames, newPrefix } = opts;

  if (oldDef) {
    for (const file of oldDef.skillInternalFiles) {
      for (const name of skillNames) {
        const fullPath = `${newPrefix}${name}/${file.subPath}`;
        if (store.filePaths.has(fullPath)) {
          const parentFolder = fullPath.split("/").slice(0, -1).join("/");
          store.deleteNode(parentFolder, "folder");
        }
      }
    }
  }

  if (!newDef) {
    return;
  }

  for (const file of newDef.skillInternalFiles) {
    for (const name of skillNames) {
      const fullPath = `${newPrefix}${name}/${file.subPath}`;
      if (!store.filePaths.has(fullPath)) {
        const skillMdPath = `${newPrefix}${name}/${SkillCollectionFileName.SkillMd}`;
        const skillContent = store.contentByPath.get(skillMdPath) ?? "";
        const meta = parseSkillMeta(skillContent);
        store.createFile(fullPath, { initialContent: file.getContent(meta) });
      }
    }
  }
};

const scaffoldInfrastructure = (
  store: SkillCollectionEditorStore,
  def: (typeof PROVIDER_CONFIGS)[SkillCollectionPreset],
): void => {
  const configFile = def.presetFiles.find(file => file.path === def.configFile);
  if (configFile && !store.filePaths.has(def.configFile)) {
    store.createFile(def.configFile, { initialContent: configFile.content });
  }

  for (const { path, content } of def.scaffoldFiles) {
    if (!store.filePaths.has(path)) {
      store.createFile(path, { initialContent: content });
    }
  }
};

export const migrateProvider = (
  from: SkillCollectionPreset | null,
  to: SkillCollectionPreset | null,
  store: SkillCollectionEditorStore,
): void => {
  if (from === to) {
    return;
  }

  runInAction(() => {
    const oldDef = from ? getProviderDef(from) : null;
    const newDef = to ? getProviderDef(to) : null;

    const skillNames = from
      ? extractSkillFolderNames(store, from)
      : extractAgnosticSkillFolderNames(store);

    const oldSkillRoot = oldDef?.skillRoot ?? null;
    const newSkillRoot = newDef?.skillRoot ?? null;
    const oldPrefix = oldSkillRoot ? `${oldSkillRoot}/` : "";
    const newPrefix = newSkillRoot ? `${newSkillRoot}/` : "";

    migrateConfigFile(store, oldDef, newDef);

    if (oldPrefix !== newPrefix) {
      if (newSkillRoot) {
        store.createFolder(newSkillRoot);
      }
      for (const name of skillNames) {
        const oldPath = oldPrefix ? `${oldPrefix}${name}` : name;
        const targetFolder = newSkillRoot ?? null;
        store.moveNode(oldPath, "folder", targetFolder);
      }
    }

    if (oldDef) {
      deleteInfrastructure(store, oldDef.infrastructurePaths);
    }

    convertSkillInternals(store, oldDef, { newDef, skillNames, newPrefix });

    if (newDef) {
      scaffoldInfrastructure(store, newDef);
    }
  });
};
